/** Small labelled inputs shared across the panes in the left rail. */

export function NumberField({
  label,
  value,
  onChange,
  suffix,
  step = 1000,
  min = 0,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  step?: number;
  min?: number;
  hint?: string;
}) {
  return (
    <label className="block" title={hint}>
      <span className="text-[10px] font-medium uppercase tracking-wider text-ink-soft">
        {label}
      </span>
      <span className="mt-0.5 flex items-center gap-1 rounded border border-line bg-surface px-1.5 focus-within:border-accent">
        <input
          type="number"
          value={value}
          min={min}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full min-w-0 bg-transparent py-1 text-right text-[12px] tabular-nums outline-none"
        />
        {suffix && <span className="shrink-0 text-[10px] text-ink-soft">{suffix}</span>}
      </span>
    </label>
  );
}

export function Choice<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-ink-soft">
        {label}
      </div>
      <div className="flex rounded-lg border border-line p-0.5">
        {options.map((o) => (
          <button
            key={String(o.value)}
            onClick={() => onChange(o.value)}
            className={`flex-1 rounded-md px-1 py-1 text-[11px] leading-tight transition-colors ${
              value === o.value
                ? "bg-accent font-medium text-white"
                : "text-ink-soft hover:bg-surface-alt"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The chevron on a collapsible section.
 *
 * An SVG rather than a text triangle: at 10px a "▸" glyph renders as a smudge
 * that varies by platform, and it cannot turn. This one rotates into place when
 * the section opens, so the control says which way it is pointing.
 */
function Chevron() {
  // The rotation is a plain rule in index.css rather than a `group-open:`
  // utility — one selector that reads as what it does, and does not depend on
  // a variant being generated for a class that only appears here.
  return (
    <svg
      viewBox="0 0 12 12"
      aria-hidden="true"
      className="chevron h-3.5 w-3.5 shrink-0 text-ink"
    >
      <path
        d="M4.5 2.5 L8 6 L4.5 9.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Shared chrome so a collapsible and a fixed section read as the same object. */
const CARD = "mt-2 overflow-hidden rounded-lg border border-line";
const HEAD =
  "flex items-center gap-1.5 border-b border-line bg-surface-alt px-2.5 py-1.5";
const TITLE = "text-[10px] font-medium uppercase tracking-wider";
const BODY = "space-y-2.5 px-2.5 py-2.5";

/** A collapsed group of secondary knobs. */
export function Section({
  title,
  summary,
  children,
}: {
  title: string;
  summary?: string;
  children: React.ReactNode;
}) {
  return (
    <details className={`group ${CARD}`}>
      {/*
        `list-none` alone leaves Safari's own triangle in place — it draws it
        through ::-webkit-details-marker, which needs hiding separately.
      */}
      <summary
        className={`${HEAD} cursor-pointer list-none hover:bg-line/40 [&::-webkit-details-marker]:hidden`}
      >
        <Chevron />
        <span className={`${TITLE} text-ink`}>{title}</span>
        {summary && (
          <span className="ml-auto truncate text-[10px] tabular-nums text-ink-soft">
            {summary}
          </span>
        )}
      </summary>
      <div className={BODY}>{children}</div>
    </details>
  );
}

/** A section that is always open — same card, no control. */
export function Panel({
  title,
  summary,
  children,
}: {
  title: string;
  summary?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={CARD}>
      <div className={HEAD}>
        <span className={`${TITLE} text-ink`}>{title}</span>
        {summary && (
          <span className="ml-auto truncate text-[10px] tabular-nums text-ink-soft">
            {summary}
          </span>
        )}
      </div>
      <div className={BODY}>{children}</div>
    </section>
  );
}

/**
 * A native select, for choices too numerous to sit in a row of buttons.
 *
 * The segmented `Choice` above stops working past three or four options in a
 * 21.5rem rail; a select keeps the label and the current value on one line and
 * costs nothing until it is opened.
 */
export function Select<T extends string | number>({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  hint?: string;
}) {
  return (
    <label className="block" title={hint}>
      <span className="text-[10px] font-medium uppercase tracking-wider text-ink-soft">
        {label}
      </span>
      <select
        value={String(value)}
        onChange={(e) => {
          const picked = options.find((o) => String(o.value) === e.target.value);
          if (picked) onChange(picked.value);
        }}
        className="mt-0.5 w-full cursor-pointer rounded border border-line bg-surface px-1.5 py-1 text-[12px] outline-none focus:border-accent"
      >
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A section heading inside a pane. */
export function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wider text-ink-soft">
      {children}
    </h3>
  );
}

/**
 * One figure in a KPI row.
 *
 * Promoted here once a fourth pane wanted it. `sub` is a second line under the
 * number; `hint` is the tooltip, for panes that keep their explanation off
 * screen — most of them do.
 */
export function Figure({
  label,
  value,
  sub,
  hint,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-lg border border-line px-2 py-1.5" title={hint}>
      <div className="truncate text-[9px] uppercase tracking-wider text-ink-soft">
        {label}
      </div>
      <div
        className={`mt-0.5 text-[14px] font-semibold tabular-nums ${
          accent ? "text-accent" : ""
        }`}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-0.5 truncate text-[9px] leading-snug text-ink-soft">
          {sub}
        </div>
      )}
    </div>
  );
}
